
import Vue from 'vue'
import axios from 'axios'
import JobInfoWidget from '@/domains/jobs/components/JobInfoWidget.vue'
import SectionHeader from '@/domains/global/components/SectionHeader.vue'
import ConfirmModal from '@/domains/global/components/ConfirmModal.vue'
import FileUpload from './FileUpload.vue'
import FileList from './FileList.vue'
import { TranslationActivity, TranslationActivitySource } from '@/domains/global/models'
import { TranslationFileDetails } from '../../activities.types'

export default Vue.extend({
  components: {
    ConfirmModal,
    FileList,
    FileUpload,
    SectionHeader,
    JobInfoWidget
  },
  props: {
    activity: { type: Object as () => TranslationActivity, required: true },
    translationTitle: { type: String, required: true }
  },
  data() {
    return {
      countdownTimer: null as any,
      isInvalidFileUploaded: false,
      isUploadingFiles: false,
      selectedDocumentToRemove: null as TranslationActivitySource | null,
      selectedFiles: [] as TranslationFileDetails[],
      showConfirmRemove: false
    }
  },
  computed: {
    disableFinish(): boolean {
      return this.selectedFiles.length === 0 || this.selectedFiles.some(i => i.status !== 'processed')
    },
    processingPdfs(): boolean {
      return this.selectedFiles.some(i => ['uploading', 'pending', 'processing'].includes(i.status as string))
    },
    jobId(): string {
      return this.activity.jobId
    },
    translationId(): string {
      return this.activity.id
    }
  },
  watch: {
    activity: {
      deep: true,
      handler(newActivity: TranslationActivity) {
        this.selectedFiles = newActivity.translationSources?.map((item) => {
          return { id: item.id, name: item.fileName, status: item.status }
        }) || []
      }
    }
  },
  mounted() {
    this.initSourceAutoPoll()
  },
  beforeDestroy() {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer)
    }
  },
  methods: {
    initSourceAutoPoll() {
      this.$emit('reload')

      if (!this.countdownTimer) {
        this.countdownTimer = setInterval(() => {
          this.$emit('reload')
          if (!this.processingPdfs && this.countdownTimer !== null) {
            clearInterval(this.countdownTimer)
            this.countdownTimer = null
          }
        }, 3000) as unknown as number;
      }
    },
    updateFiles(files: TranslationFileDetails[]) {
      this.isInvalidFileUploaded = false
      this.isUploadingFiles = true
      const formData = new FormData()
      files.forEach((item) => {
        if (item.file) {
          formData.append('files', item.file)
        }
      })

      axios.put(`/jobs/${this.jobId}/activities/${this.translationId}/sources`, formData).then(() => {
        this.isUploadingFiles = false
        this.initSourceAutoPoll()
      }).catch((err) => {
        this.handleUploadError(err)
      })
    },
    handleUploadError(err: any) {
      if (err.response.status === 422) {
        this.isInvalidFileUploaded = true
      } else if (err.response.status === 409) {
        if (err.response.data && err.response.data.message) {
          this.$store.dispatch('error', err.response.data.message)
        } else {
          this.$store.dispatch('error', 'Unable to upload files')
        }
      } else {
        this.$store.dispatch('error', 'Error uploading documents. Please try again')
      }
      this.isUploadingFiles = false
    },
    removeSource(data: TranslationActivitySource) {
      this.showConfirmRemove = true
      this.selectedDocumentToRemove = data
    },
    async confirmRemoveDocument() {
      try {
        await axios.delete(`/jobs/${this.jobId}/activities/${this.translationId}/sources/${this.selectedDocumentToRemove?.id}`)
        this.showConfirmRemove = false
        this.initSourceAutoPoll()
        this.$store.dispatch('success', 'Deleted successfully')
      } catch (err) {
        const error = err as any
        this.showConfirmRemove = false
        if (error.response.status === 404) {
          this.$store.dispatch('error', 'You cannot remove sources from this translation')
        }
      }
    }
  }
})
